@if (submitted()) {
  <!-- CONFIRMATION: the counselor's standing instructions, shown once the
       request is in. Only reached when they actually have instructions. -->
  <h2 mat-dialog-title class="dialog-title">Request sent</h2>

  <mat-dialog-content>
    <p class="confirmation-lead">
      Your request has been sent to {{ submittedCounselorName() }}. You'll be notified once they
      respond.
    </p>

    <div class="counselor-instructions" data-testid="counselor-instructions">
      <div class="counselor-instructions-header">
        <mat-icon>info_outline</mat-icon>
        <span>Instructions from faculty</span>
      </div>
      <p class="counselor-instructions-body">{{ counselorInstructions }}</p>
    </div>

    <p class="confirmation-note">You can find these again any time under the meeting's details.</p>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button
      mat-flat-button
      color="primary"
      type="button"
      data-testid="instructions-done-btn"
      (click)="finish()"
    >
      Got it
    </button>
  </mat-dialog-actions>
} @else {
  <h2 mat-dialog-title class="dialog-title">Request a meeting</h2>

  <mat-dialog-content>
    <div class="dialog-layout">
      <!-- LEFT: counselor, duration, reason -->
      <div class="dialog-left">
        @if (data.counselors.length >= 1) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Counselor</mat-label>
            <mat-select
              data-testid="counselor-select"
              [formControl]="form.controls.counselorId"
              (selectionChange)="onCounselorChange()"
            >
              @for (c of data.counselors; track c.id) {
                <mat-option [value]="c.id" [attr.data-testid]="'counselor-option-' + c.id">{{
                  c.name
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Meeting length</mat-label>
          <mat-select
            data-testid="duration-select"
            [formControl]="form.controls.durationMinutes"
            (selectionChange)="onDurationChange()"
          >
            @for (d of allowedDurations(); track d) {
              <mat-option [value]="d" [attr.data-testid]="'duration-option-' + d"
                >{{ d }} min</mat-option
              >
            }
          </mat-select>
          @if (!form.controls.counselorId.value) {
            <mat-hint>Select a faculty member first</mat-hint>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Meeting format</mat-label>
          <mat-select data-testid="meeting-type-select" [formControl]="form.controls.meetingType">
            <mat-option value="remote" data-testid="meeting-type-option-remote"
              >Remote (online)</mat-option
            >
            <mat-option value="in-person" data-testid="meeting-type-option-in-person"
              >In person</mat-option
            >
          </mat-select>
        </mat-form-field>

        @if (form.controls.titleTopic.value !== 'Other') {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Subject</mat-label>
            <mat-select
              data-testid="subject-select"
              [formControl]="form.controls.titleTopic"
              (selectionChange)="onTitleTopicChange()"
            >
              @for (topic of titleTopics; track topic) {
                <mat-option [value]="topic" [attr.data-testid]="'subject-option-' + topic">{{
                  topic
                }}</mat-option>
              }
            </mat-select>
            @if (
              form.controls.titleTopic.hasError('required') && form.controls.titleTopic.touched
            ) {
              <mat-error>Pick a subject.</mat-error>
            }
          </mat-form-field>
        } @else {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Subject</mat-label>
            <input
              matInput
              data-testid="subject-other-input"
              [formControl]="form.controls.titleOther"
              maxlength="50"
              placeholder="Other"
            />
            <button
              matSuffix
              mat-icon-button
              type="button"
              data-testid="subject-other-back-btn"
              aria-label="Choose from the list instead"
              (click)="resetSubject()"
            >
              <mat-icon>close</mat-icon>
            </button>
            @if (
              form.controls.titleOther.hasError('required') && form.controls.titleOther.touched
            ) {
              <mat-error>Subject required.</mat-error>
            }
          </mat-form-field>
        }

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Reason / details</mat-label>
          <textarea
            matInput
            data-testid="reason-textarea"
            [formControl]="form.controls.reason"
            rows="4"
            maxlength="200"
            placeholder="Any extra context for your counselor?"
          ></textarea>
          @if (form.controls.reason.hasError('required') && form.controls.reason.touched) {
            <mat-error>Give a brief reason.</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- RIGHT: calendar or day view -->
      <div class="dialog-right">
        @if (!form.controls.durationMinutes.value) {
          <!-- Placeholder before duration is picked -->
          <div class="calendar-placeholder">
            <mat-icon>calendar_month</mat-icon>
            <span>Select a meeting length to see available dates</span>
          </div>
        } @else if (!inDayView) {
          <!-- MONTH VIEW -->
          <mat-calendar
            data-testid="request-calendar"
            [dateFilter]="dateFilter"
            [minDate]="earliestDate"
            [maxDate]="pickerMaxDate"
            [selected]="selectedDate"
            (selectedChange)="onDateSelected($event)"
          />
          @if (selectedDate) {
            <div class="see-times-row">
              <button
                mat-flat-button
                color="primary"
                data-testid="see-times-btn"
                (click)="goToDayView()"
              >
                See available times for {{ selectedDate | date: 'EEE, MMM d' }}
                <mat-icon iconPositionEnd>arrow_forward</mat-icon>
              </button>
            </div>
          }
        } @else {
          <!-- DAY VIEW -->
          <div class="day-view">
            <div class="day-view-header">
              <button
                mat-icon-button
                data-testid="back-to-month-btn"
                (click)="backToMonthView()"
                type="button"
              >
                <mat-icon>arrow_back</mat-icon>
              </button>
              <span class="day-view-title">{{ selectedDate | date: 'EEEE, MMMM d' }}</span>
            </div>

            @if (availableSlots.length) {
              <div class="slots-grid">
                @for (slot of availableSlots; track slot.time.getTime()) {
                  <button
                    class="slot-btn"
                    [attr.data-testid]="'slot-btn-' + slot.time.getTime()"
                    [class.selected]="selectedSlot?.getTime() === slot.time.getTime()"
                    [class.blocked]="slot.blocked"
                    [disabled]="slot.blocked"
                    (click)="selectSlot(slot)"
                    type="button"
                  >
                    {{ slot.time | date: 'h:mm a' }}
                  </button>
                }
              </div>
            } @else {
              <p class="no-slots">No available times on this day.</p>
            }
          </div>
        }
      </div>
    </div>
  </mat-dialog-content>

  @if (submitError()) {
    <p class="submit-error" data-testid="submit-error">{{ submitError() }}</p>
  }

  <mat-dialog-actions align="end">
    <button mat-button type="button" data-testid="cancel-btn" (click)="dialogRef.close()">
      Cancel
    </button>
    <button
      mat-flat-button
      color="primary"
      type="button"
      data-testid="submit-btn"
      [disabled]="
        submitting() ||
        !selectedSlot ||
        form.controls.titleTopic.invalid ||
        form.controls.titleOther.invalid ||
        form.controls.reason.invalid
      "
      (click)="submit()"
    >
      {{ submitting() ? 'Sending…' : 'Send request' }}
    </button>
  </mat-dialog-actions>
}
